import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/analytics"

// One overlay rendered in the public layout, opened by any element with
// data-action="click->search-overlay#open" (today: the navbar's search
// icon). Manages its own visibility — no history mutation, no navigation.
//
// Why this exists: the public layout disables Turbo Drive because the
// Botmaker (FORDI) chat widget injects an iframe via document.write and
// browsers reset such iframes on DOM moves. Without Turbo, treating
// /search as a real page meant every open AND every close was a full
// reload — closing from /search?q=foo back to /cars/X took 1-2s on
// mobile. With the overlay the underlying page never unmounts, so close
// is `display: none` — instant by construction.
//
// Stimulus targets live inside the overlay partial; the action triggers
// (navbar search icon) live in the navbar. Both are descendants of the
// <body> element this controller attaches to, so action dispatch works.
export default class extends Controller {
  static targets = ["panel", "input", "form", "results", "clearBtn", "scopeHeader"]
  static values = {
    debounce: { type: Number, default: 300 },
    endpoint: { type: String, default: "/search" }
  }

  connect() {
    this.timeout = null
    this._currentCarSlug = ""
    // Selected year / version / model carried in from the car experience
    // page — rendered in the scope header alongside the car.
    this._scope = {}
    // Busca Rápida cluster filter — set when the user taps a quick
    // suggestion, cleared the moment they type or clear the input
    // (typing wins over the playlist filter).
    this._currentClusterIds = ""
    this._boundHandleKey = this.handleKey.bind(this)
  }

  disconnect() {
    if (this.timeout) clearTimeout(this.timeout)
    document.removeEventListener("keydown", this._boundHandleKey)
  }

  // Opens the overlay. Trigger element may pass a car_slug via
  // data-search-overlay-car-slug-param to scope the search to that car
  // (set by the navbar when the user is on /cars/<slug>).
  open(event) {
    if (event) event.preventDefault()

    const carSlug = event?.params?.carSlug || ""
    this._currentCarSlug = carSlug
    this._scope = {
      year: event?.params?.scopeYear || "",
      version: event?.params?.scopeVersion || "",
      model: event?.params?.scopeModel || "",
      versionId: event?.params?.scopeVersionId || "",
      modelId: event?.params?.scopeModelId || ""
    }
    this._currentClusterIds = ""

    this.panelTarget.classList.remove("hidden")
    this.panelTarget.classList.add("flex")
    document.body.classList.add("overflow-hidden")
    document.addEventListener("keydown", this._boundHandleKey)

    // Reset state on every open — the input is blank, suggestions render.
    this.inputTarget.value = ""
    this.toggleClearBtn()
    this.fetchAndRender("")

    // Focus on the next frame so iOS Safari actually opens the keyboard
    // (focusing during the same tick as a display change is a no-op there).
    requestAnimationFrame(() => this.inputTarget.focus())
  }

  close(event) {
    if (event) event.preventDefault()
    this.panelTarget.classList.add("hidden")
    this.panelTarget.classList.remove("flex")
    document.body.classList.remove("overflow-hidden")
    document.removeEventListener("keydown", this._boundHandleKey)
    if (this.timeout) clearTimeout(this.timeout)
  }

  // Debounced input handler — fires as the user types. Typing clears
  // any pinned Busca-Rápida cluster filter; the user is shifting to
  // text search, so the playlist filter should not silently constrain
  // the new results.
  onInput() {
    this.toggleClearBtn()
    this._currentClusterIds = ""
    if (this.timeout) clearTimeout(this.timeout)
    this.timeout = setTimeout(() => {
      const query = this.inputTarget.value.trim()
      this.fetchAndRender(query)
    }, this.debounceValue)
  }

  // Form submit (Buscar button click or Enter in the input). Cancels the
  // pending debounce so we don't double-fetch, fires immediately, and
  // tags the fetch for analytics — search_performed only fires on
  // explicit submits, matching the pre-overlay behavior.
  submit(event) {
    event.preventDefault()
    if (this.timeout) clearTimeout(this.timeout)
    const query = this.inputTarget.value.trim()
    this.fetchAndRender(query, { trackPerformed: true })
  }

  clearInput() {
    this.inputTarget.value = ""
    this._currentClusterIds = ""
    this.toggleClearBtn()
    this.inputTarget.focus()
    this.fetchAndRender("")
  }

  // Drops the current car-scope filter and re-fetches. Wired to the
  // "Search all vehicles" link rendered inside the scope header partial.
  clearScope(event) {
    if (event) event.preventDefault()
    this._currentCarSlug = ""
    const query = this.inputTarget.value.trim()
    this.fetchAndRender(query)
  }

  // Pre-fills the search input with a quick-suggestion label and fires
  // a search immediately. Each Busca-Rápida button carries cluster_ids
  // so the server can short-circuit text matching and return every
  // feature in those playlists — the playlist label ("Conectividade")
  // never matches a feature NAME on its own, so a text search against
  // the label always came back empty. Wired to the suggestion buttons
  // rendered inside the empty-state partial.
  useSuggestion(event) {
    if (event) event.preventDefault()
    const label = event.params.suggestion || ""
    this._currentClusterIds = event.params.clusterIds
      ? String(event.params.clusterIds)
      : (event.params.clusterId ? String(event.params.clusterId) : "")
    this.inputTarget.value = label
    this.toggleClearBtn()
    this.fetchAndRender(label, { trackPerformed: true })
  }

  // Fetches /search and splices the response partial into the overlay.
  // The server returns the `_results` partial — two top-level wrappers
  // ([data-search-scope-header] and [data-search-results-section]) are
  // what we extract and inject.
  async fetchAndRender(query, opts = {}) {
    const params = new URLSearchParams()
    if (query) params.set("q", query)
    if (this._currentCarSlug) {
      params.set("car_slug", this._currentCarSlug)
      // Scope breadcrumb only travels with the car scope — dropping the
      // car (clearScope) drops these too.
      if (this._scope?.year) params.set("scope_year", this._scope.year)
      if (this._scope?.version) params.set("scope_version", this._scope.version)
      if (this._scope?.model) params.set("scope_model", this._scope.model)
      if (this._scope?.versionId) params.set("scope_version_id", this._scope.versionId)
      if (this._scope?.modelId) params.set("scope_model_id", this._scope.modelId)
    }
    if (this._currentClusterIds) params.set("cluster_ids", this._currentClusterIds)
    const qs = params.toString()
    const url = qs ? `${this.endpointValue}?${qs}` : this.endpointValue

    try {
      const response = await fetch(url, {
        headers: { "Accept": "text/html", "X-Requested-With": "XMLHttpRequest" }
      })
      const html = await response.text()
      const doc = new DOMParser().parseFromString(html, "text/html")

      const results = doc.querySelector("[data-search-results-section]")
      if (results && this.hasResultsTarget) {
        this.resultsTarget.innerHTML = results.innerHTML
      }

      const scope = doc.querySelector("[data-search-scope-header]")
      if (this.hasScopeHeaderTarget) {
        this.scopeHeaderTarget.innerHTML = scope ? scope.innerHTML : ""
      }

      if (opts.trackPerformed && query) {
        const totalEl = doc.querySelector("[data-search-total]")
        const count = totalEl ? parseInt(totalEl.dataset.searchTotal, 10) : 0
        track("search_performed", { query, results_count: count })
      }
    } catch (_) { /* silent — see analytics.js convention */ }
  }

  handleKey(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    }
  }

  toggleClearBtn() {
    if (!this.hasClearBtnTarget || !this.hasInputTarget) return
    if (this.inputTarget.value.length > 0) {
      this.clearBtnTarget.classList.remove("hidden")
    } else {
      this.clearBtnTarget.classList.add("hidden")
    }
  }
}
